import moment from 'moment';

import {
  SCHEDULED_ORDER_DATE_FORMAT,
  SCHEDULED_ORDER_MERIDIEM,
  ScheduleTab,
} from './scheduleOrder.const';

import type { ScheduledOrder, ScheduledOrderDetailTopping } from './models';

export function isScheduleTab(value: unknown): value is ScheduleTab {
  return value === ScheduleTab.InProgress || value === ScheduleTab.Scheduled;
}

export function resolveSelectedStores(
  selectedStoreIds: (number | string)[],
  partnerStores: { id: number }[],
): number[] {
  const ids =
    selectedStoreIds.length > 0
      ? selectedStoreIds
      : partnerStores.map(s => s.id);
  return ids.map(id => +id);
}

export function formatScheduledDate(
  dateStr: string,
  meridiem: { am: string; pm: string } = SCHEDULED_ORDER_MERIDIEM,
): string {
  const date = moment(dateStr, SCHEDULED_ORDER_DATE_FORMAT);
  const period = date.hours() < 12 ? meridiem.am : meridiem.pm;
  return `${date.format('DD/MM/YYYY')} · ${date.format('h:mm')} ${period}`;
}

export function sortByPlaceAtAsc(orders: ScheduledOrder[]): ScheduledOrder[] {
  return [...orders].sort(
    (a, b) =>
      moment(a.placeAt, SCHEDULED_ORDER_DATE_FORMAT).valueOf() -
      moment(b.placeAt, SCHEDULED_ORDER_DATE_FORMAT).valueOf(),
  );
}

/**
 * Returns the parts of the scheduled banner phrase separately so the i18n
 * template can interpolate them: { date: 'DD/MM/YYYY', time: 'h:mm a.m./p.m.' }
 */
export function formatScheduledBannerDate(
  dateStr: string,
  meridiem: { am: string; pm: string } = SCHEDULED_ORDER_MERIDIEM,
): { date: string; time: string } {
  const date = moment(dateStr, SCHEDULED_ORDER_DATE_FORMAT);
  const period = date.hours() < 12 ? meridiem.am : meridiem.pm;
  return {
    date: date.format('DD/MM/YYYY'),
    time: `${date.format('h:mm')} ${period}`,
  };
}

export interface ScheduledOrderToppingGroup {
  categoryId: number;
  categoryDescription: string;
  toppings: ScheduledOrderDetailTopping[];
}

export function groupToppingsByCategory(
  toppings: ScheduledOrderDetailTopping[],
): ScheduledOrderToppingGroup[] {
  const map = new Map<number, ScheduledOrderToppingGroup>();

  for (const topping of toppings) {
    const existing = map.get(topping.categoryId);
    if (existing) {
      existing.toppings.push(topping);
    } else {
      map.set(topping.categoryId, {
        categoryId: topping.categoryId,
        categoryDescription: topping.categoryDescription,
        toppings: [topping],
      });
    }
  }

  return Array.from(map.values());
}
